import { CSSProperties } from 'react';
import { useRecoilValue } from 'recoil';
import { FixedSizeList as List } from 'react-window';
import BootstrapForm from 'react-bootstrap/Form';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpIcon from '@mui/icons-material/Help';

import * as K from 'common/constants';
import { HELP_ROUTE } from 'components/help/constants';
import ELEMENT from 'common/element';
import { UrlParams } from 'common/types';
import { getDateRangeText, getFormattedTimeOffsetUTC, getTimezoneOffset, timezoneMoment } from 'common/util/time';
import { buildUrl } from 'common/util/location';
import { useIsLabEnabled } from 'hooks/useIsLabEnabled';

import Page from 'components/common/Page/Page';
import Dropdown from 'components/common/Dropdown/Dropdown';
import TabbedPageHeader from 'components/common/TabbedPageHeader/TabbedPageHeader';
import Tabs from 'components/common/Tabs/Tabs';
import TabContents from 'components/common/TabContents/TabContents';
import TabContent from 'components/common/TabContent/TabContent';
import Button from 'components/common/Button/Button';
import Toolbar from 'components/common/Toolbar/Toolbar';
import LoadingPlaceholder from 'components/common/LoadingPlaceholder/LoadingPlaceholder';
import ErrorPlaceholder from 'components/common/ErrorPlaceholder/ErrorPlaceholder';
import AlertListToolbar from 'components/alerts/AlertListPage/AlertListToolbar';
import AlertListItem from 'components/alerts/AlertListPage/AlertListItem';
import AddAlertButton from 'components/alerts/AddAlertButton';
import useAlerts from 'components/alerts/AlertListPage/useAlerts';
import ClearableInput from 'components/common/ClearableInput/ClearableInput';
import { windowHeightState } from 'components/common/Managers/LayoutStateManager';
import Link from 'components/common/Link/Link';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';

import './AlertListPage.scss';

const ALERT_TABS = {
  TRIGGERED: 'triggered',
  MUTED: 'muted',
  INCOMPLETE: 'incomplete',
  ALL: 'all',
};

const ALERT_ITEM_SIZE = 65;
const BOTTOM_BUFFER = 220;

function AlertListPage(): JSX.Element {
  const { alertModel, loading, error } = useAlerts();
  const windowHeight = useRecoilValue(windowHeightState);
  const timezone = useRecoilValue(timezoneState);
  const timezoneOffset = getTimezoneOffset(timezone);
  const isPQAlertsLabEnabled = useIsLabEnabled(K.LAB.PQ_ALERTS);

  if (loading) {
    return <LoadingPlaceholder />;
  }

  if (error) {
    return <ErrorPlaceholder error={error} />;
  }

  const tabs = [
    {
      title: 'Triggered',
      badge: alertModel.triggeredAlerts.length,
      eventKey: ALERT_TABS.TRIGGERED,
      testId: ELEMENT.alertTriggeredTab,
    },
    {
      title: 'Muted',
      badge: alertModel.mutedAlerts.length,
      eventKey: ALERT_TABS.MUTED,
      testId: ELEMENT.alertMutedTab,
    },
    {
      title: 'All',
      badge: alertModel.alerts.length,
      eventKey: ALERT_TABS.ALL,
      testId: ELEMENT.alertAllTab,
    },
  ];

  if (isPQAlertsLabEnabled) {
    tabs.push({
      title: 'Incomplete',
      badge: alertModel.incompleteAlerts.length,
      eventKey: ALERT_TABS.INCOMPLETE,
      testId: ELEMENT.alertIncompleteTab,
    });
  }

  const alertJSONUrl = buildUrl(K.ROUTE.CONFIG_FILE, {
    [K.URL_PARAM.PATH]: '/scalyr/alerts',
  } as UrlParams);
  const settingsButtonSection = [
    [
      {
        as: Link,
        text: 'Alerts JSON',
        href: alertJSONUrl,
        testId: ELEMENT.alertJSONLink,
      },
    ],
  ];

  const onSearchInputChange = (value: string): void => {
    alertModel.setSearchText(value);
  };

  const filterFragment = (
    <>
      <BootstrapForm inline>
        <BootstrapForm.Label>Filter:</BootstrapForm.Label>
        <ClearableInput
          value={alertModel.searchText}
          className="AlertListPage__FilterInput"
          size="sm"
          onChange={onSearchInputChange}
          testId={ELEMENT.alertFilterInput}
        />
      </BootstrapForm>
    </>
  );
  const settingsIcon = <SettingsIcon fontSize="small" />;
  const settingsButtonFragment = (
    <Dropdown
      className="AlertListPage__SettingsDropdown"
      sections={settingsButtonSection}
      leftIcon={settingsIcon}
      testId={ELEMENT.alertSettingsDropdown}
    >
      Settings
    </Dropdown>
  );
  const helpButtonFragment = (
    <Button
      href={HELP_ROUTE.HELP_ALERTS}
      size="sm"
      leftIcon={<HelpIcon fontSize="small" />}
      openInNewTab
      testId={ELEMENT.alertHelpButton}
    >
      Help
    </Button>
  );

  const addAlertFragment = <AddAlertButton />;

  const headerItems = [filterFragment, settingsButtonFragment, helpButtonFragment, addAlertFragment];

  const AlertItem = ({ index, style }: { index: number; style?: CSSProperties }): JSX.Element => {
    const alert = alertModel.alerts[index];
    return <AlertListItem alertModel={alertModel} alert={alert} key={alert.alertId} style={style} index={index} />;
  };

  const MutedAlert = ({ index, style }: { index: number; style?: CSSProperties }): JSX.Element => {
    const alert = alertModel.mutedAlerts[index];
    return <AlertListItem alertModel={alertModel} alert={alert} key={alert.alertId} style={style} index={index} />;
  };

  const TriggeredAlert = ({ index, style }: { index: number; style?: CSSProperties }): JSX.Element => {
    const alert = alertModel.triggeredAlerts[index];
    return <AlertListItem alertModel={alertModel} alert={alert} key={alert.alertId} style={style} index={index} />;
  };

  const IncompleteAlert = ({ index, style }: { index: number; style?: CSSProperties }): JSX.Element => {
    const alert = alertModel.incompleteAlerts[index];
    return <AlertListItem alertModel={alertModel} alert={alert} key={alert.alertId} style={style} index={index} />;
  };

  const panes = [
    { eventKey: ALERT_TABS.TRIGGERED, itemCount: alertModel.triggeredAlerts.length, alertListItem: TriggeredAlert },
    { eventKey: ALERT_TABS.MUTED, itemCount: alertModel.mutedAlerts.length, alertListItem: MutedAlert },
    { eventKey: ALERT_TABS.ALL, itemCount: alertModel.alerts.length, alertListItem: AlertItem },
  ];

  if (isPQAlertsLabEnabled) {
    panes.push({
      eventKey: ALERT_TABS.INCOMPLETE,
      itemCount: alertModel.incompleteAlerts.length,
      alertListItem: IncompleteAlert,
    });
  }

  const end = timezoneMoment(timezone)();
  const start = timezoneMoment(timezone)().subtract(1, 'hour');
  const dateRangeText = getDateRangeText(start.toDate(), end.toDate(), timezone, true);
  const UTCOffset = getFormattedTimeOffsetUTC(timezoneOffset);
  const formattedTimeStamp = dateRangeText + ' [' + UTCOffset + ']';

  const EmptyListState = (
    <div className="AlertListPage__EmptyListState">
      <span>No alerts found</span>
    </div>
  );

  return (
    <Page docTitle="Alerts" testId={ELEMENT.alertsPage}>
      <Tabs defaultActiveTab={ALERT_TABS.TRIGGERED}>
        <TabbedPageHeader
          title="Alerts"
          tabs={tabs}
          className="AlertListPage__TabbedHeader"
          headerItems={[<Toolbar key="AlertListPage__Toolbar" items={headerItems} />]}
        />
        <div className="AlertListPage__ActionListHeaders">
          <AlertListToolbar alertModel={alertModel} className="AlertListPage__ToolbarHeader" />
          <div className="AlertListPage__TimeStampHeader" data-test-id={ELEMENT.alertTimestampHeader}>
            {formattedTimeStamp}
          </div>
        </div>

        <TabContents>
          {panes.map((pane) => (
            <TabContent
              key={pane.eventKey}
              eventKey={pane.eventKey}
              onEnter={(): void => {
                alertModel.setActiveTab(pane.eventKey);
                alertModel.unselectAlerts();
              }}
            >
              {pane.itemCount > 0 && alertModel.activeTab === pane.eventKey ? (
                <List
                  height={windowHeight - BOTTOM_BUFFER}
                  width="100%"
                  itemCount={pane.itemCount}
                  itemSize={ALERT_ITEM_SIZE}
                >
                  {pane.alertListItem}
                </List>
              ) : (
                EmptyListState
              )}
            </TabContent>
          ))}
        </TabContents>
      </Tabs>
    </Page>
  );
}

export default AlertListPage;
